import Axios, { isCancel } from 'axios';
import { API_URL, MS_URL, CLIENT_ID, API_SCOPES } from './Constants.js';
import { PublicClientApplication } from '@azure/msal-browser';
import { loginRequest, msalConfig } from "../../authConfig";
import { decodeAccessToken, createCustomOidToken, getAzureTokenId } from './tokenUtils';
import { clearAuthStorage, safeGetLocalStorage, safeSetLocalStorage } from './storageUtils';


const msalInstance = new PublicClientApplication(msalConfig);

const apiService = {
  async post(url, payload) {
    const customToken = localStorage.getItem('accessToken');
    
    if (!customToken) {
      return Promise.reject({ message: 'No access token available - redirecting to authentication' });
    }

    const headers = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${customToken}`  // Custom token with oid, exp, ati (no PII, no Azure token)
      }
    };
    
    return await Axios.post(`${API_URL}/${url}`, payload, headers).catch(function (error) {
      if (error.response) {
        if (error.response.data.status_code == 401 && error.response.data.message == 'Unauthorized') {
          clearAuthStorage();
          window.location.href = MS_URL;
        }
        return error.response;
      } else {
        console.log('Error', error.message);
      }
    })
      .then(response => {
        if (localStorage.getItem('user.role') === undefined || localStorage.getItem('user.role') === null) {
          localStorage.setItem('user.role', response.headers['role']);
        }
        return response;
      });
  },

  async get(url, payload = {}) {
    const customToken = localStorage.getItem('accessToken');
    
    if (!customToken) {
      return Promise.reject({ message: 'No access token available - redirecting to authentication' });
    }

    const headers = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${customToken}`  // Custom token with oid, exp, ati (no PII, no Azure token)
      }
    };

    return await Axios.get(`${API_URL}/${url}`, {
      ...headers,
      params: payload?.params || {}
    })
      .catch(function (error) {
        if (error.response) {
          if (
            error.response.data.status_code == 401 &&
            error.response.data.message == "Unauthorized"
          ) {
            clearAuthStorage();
            window.location.href = MS_URL;
          }
          return error.response;
        } else {
          console.log("Error", error.message);
        }
      })
      .then((response) => {
        if (
          localStorage.getItem("user.role") === undefined ||
          localStorage.getItem("user.role") === null
        ) {
          localStorage.setItem("user.role", response.headers["role"]);
        }
        return response;
      });
  },

  async postMedia(url, payload, options) {
    try {
      // Get custom token from localStorage (stored during login, refreshed by tokenRefreshService)
      const customToken = localStorage.getItem('accessToken');
      
      if (!customToken) {
        return Promise.reject({ message: 'No access token available - redirecting to authentication' });
      }

      const headers = {
        headers: {
          'Authorization': `Bearer ${customToken}`,  // Custom token with oid, exp, ati (no PII, no Azure token)
          ...(options?.headers || {})
        }
      };
      
      // Merge headers and options into a single object
      const requestConfig = {
        ...headers,
        ...options
      };
      return await Axios.post(`${API_URL}/${url}`, payload, requestConfig).catch(function (error) {
        if (error.response) {
          console.log(error.response.data);
          // Handle backend rejection of tokens
          if (error.response.data?.error === 'Invalid token type') {
            console.error('Backend rejected token. Clearing session...');
            clearAuthStorage();
            window.location.href = MS_URL;
            return error.response;
          }
          if (error.response.data.status_code == 401 && error.response.data.message == 'Unauthorized') {
            clearAuthStorage();
            window.location.href = MS_URL;
          }
          return error.response;
        } else {
          console.log('Error', error.message);
        }
      })
        .then(response => {
          if (localStorage.getItem('user.role') === undefined || localStorage.getItem('user.role') === null) {
            localStorage.setItem('user.role', response.headers['role']);
          }
          return response;
        });;
    } catch (err) {
      if (Axios.isCancel(err)) {
        console.log("I am cancelled");
      }
    }
  },

  /**
   * Extracts oid from access token and stores it in localStorage
   * ARCHITECTURE: Frontend extracts oid from JWT token, stores ONLY oid in localStorage
   * The actual Azure token is NEVER stored in localStorage for security
   * 
   * Supports both flows:
   * 1. App flow (/login?client_id=...): Gets fresh token from backend, extracts oid
   * 2. User flow (MSAL): Extracts oid from MSAL user token
   */
  async extractAndStoreOid() {
    const clientId = localStorage.getItem('clientId');
    
    // App flow: Get fresh token and extract oid (don't store Azure token)
    if (clientId) {
      try {
        console.log('App flow detected: Getting fresh token to extract oid');
        const tokenData = await this._getAppFlowTokenData(clientId);
        
        if (tokenData && tokenData.oid) {
          console.log('OID extracted from app flow token');
          return tokenData.oid;
        } else {
          console.error('Could not get oid from app flow');
          return null;
        }
      } catch (error) {
        console.error('Error extracting oid from app flow:', error);
        return null;
      }
    }

    // User flow: Get token from MSAL and extract oid
    try {
      const tokenData = await this._getUserFlowTokenData();
      if (tokenData && tokenData.oid) {
        console.log('OID extracted from user flow token');
        return tokenData.oid;
      }
      return null;
    } catch (error) {
      console.error('Error extracting oid from user flow:', error);
      window.location.href = MS_URL;
      return null;
    }
  },

  /**
   * Gets oid from localStorage or extracts it from token if not available
   * Supports both app flow (clientId exists) and user flow (MSAL)
   * @returns {Promise<string|null>} User oid or null if not available
   */
  async getOid() {
    // Check if oid is already stored
    const storedOid = localStorage.getItem('userOid');
    if (storedOid) {
      return storedOid;
    }
    
    // Extract oid from token (handles both app flow and user flow)
    return await this.extractAndStoreOid();
  },

  /**
   * Creates a custom token with oid and Azure token metadata for Authorization header
   * ARCHITECTURE: Frontend creates custom token containing oid (no PII)
   * This custom token is sent in Authorization header instead of original JWT token
   * 
   * Custom token uses Azure token's expiry time and unique ID (uti/jti) for:
   * - Synchronized expiry with Azure token
   * - Token correlation/tracking on backend
   * 
   * @returns {Promise<string|null>} Custom token string or null if oid not available
   */
  async getCustomOidToken() {
    // Check if this is an app flow (clientId exists in localStorage)
    const clientId = localStorage.getItem('clientId');
    const isAppFlow = !!clientId;

    let oid, azureTokenExp, azureTokenId;

    if (isAppFlow) {
      // App flow: Get fresh token from backend (don't store Azure token)
      const tokenData = await this._getAppFlowTokenData(clientId);
      if (!tokenData) {
        return null;
      }
      oid = tokenData.oid;
      azureTokenExp = tokenData.exp;
      azureTokenId = tokenData.ati;
    } else {
      // User flow: Get token from MSAL
      const tokenData = await this._getUserFlowTokenData();
      if (!tokenData) {
        return null;
      }
      oid = tokenData.oid;
      azureTokenExp = tokenData.exp;
      azureTokenId = tokenData.ati;
    }

    if (!oid) {
      return null;
    }

    // Create custom token with Azure token expiry and unique ID
    return createCustomOidToken({
      oid: oid,
      azureTokenExp: azureTokenExp,
      azureTokenId: azureTokenId,
      isAppFlow: isAppFlow,
      clientId: isAppFlow ? clientId : null
    });
  },

  /**
   * Gets token data for user flow (MSAL)
   * Does NOT store Azure token in localStorage - only extracts needed claims
   * 
   * @returns {Promise<{oid: string, exp: number, ati: string}|null>} Token data or null
   */
  async _getUserFlowTokenData() {
    try {
      const activeAccount = msalInstance.getActiveAccount();
      const accounts = msalInstance.getAllAccounts();

      if (!activeAccount && accounts.length === 0) {
        console.warn('No MSAL account found');
        window.location.href = MS_URL;
        return null;
      }

      const account = activeAccount || accounts[0];
      const request = {
        ...loginRequest,
        account: account
      };

      // Acquire token silently - MSAL handles caching internally
      const authResult = await msalInstance.acquireTokenSilent(request);
      
      if (!authResult.accessToken) {
        throw new Error('Access token not available');
      }

      // Decode token to extract claims (DO NOT store the token)
      const decoded = decodeAccessToken(authResult.accessToken);
      if (!decoded || !decoded.oid) {
        throw new Error('Could not extract oid from access token');
      }

      // Store only non-sensitive identifiers (for session tracking)
      localStorage.setItem('userOid', decoded.oid);
      if (decoded.tid) {
        localStorage.setItem('userTid', decoded.tid);
      }

      return {
        oid: decoded.oid,
        exp: decoded.exp,
        ati: getAzureTokenId(decoded)
      };
    } catch (error) {
      console.error('Error getting user flow token data:', error);
      
      if (error.name === 'InteractionRequiredAuthError') {
        window.location.href = MS_URL;
      }
      
      return null;
    }
  },

  /**
   * Gets token data for app flow (backend /login)
   * Does NOT store Azure token in localStorage - only extracts needed claims
   * 
   * @param {string} clientId - Client ID for app flow
   * @returns {Promise<{oid: string, exp: number, ati: string}|null>} Token data or null
   */
  async _getAppFlowTokenData(clientId) {
    try {
      // Check if we have cached token data that hasn't expired
      const cachedExp = sessionStorage.getItem('appFlowTokenExp');
      const cachedOid = localStorage.getItem('userOid');
      const cachedAti = sessionStorage.getItem('appFlowTokenAti');
      
      if (cachedExp && cachedOid) {
        const expTime = parseInt(cachedExp, 10);
        const now = Math.floor(Date.now() / 1000);
        
        // If token expires in more than 5 minutes, use cached data
        if (expTime - now > 300) {
          return {
            oid: cachedOid,
            exp: expTime,
            ati: cachedAti
          };
        }
      }

      // Get fresh token from backend
      console.log('App flow: Getting fresh token from backend');
      const response = await Axios.post(`${API_URL}/login?client_id=${clientId}`);
      
      if (!response.data || !response.data.accessToken) {
        throw new Error('Failed to get token from backend');
      }

      // Decode to extract claims (DO NOT store the actual Azure token)
      const decoded = decodeAccessToken(response.data.accessToken);
      if (!decoded || !decoded.oid) {
        throw new Error('Could not extract oid from app token');
      }

      // Store only non-sensitive identifiers (NOT the actual Azure token)
      localStorage.setItem('userOid', decoded.oid);
      if (decoded.tid) {
        localStorage.setItem('userTid', decoded.tid);
      }
      
      // Cache expiry and token ID in sessionStorage (cleared on browser close)
      sessionStorage.setItem('appFlowTokenExp', decoded.exp.toString());
      if (getAzureTokenId(decoded)) {
        sessionStorage.setItem('appFlowTokenAti', getAzureTokenId(decoded));
      }

      return {
        oid: decoded.oid,
        exp: decoded.exp,
        ati: getAzureTokenId(decoded)
      };
    } catch (error) {
      console.error('Error getting app flow token data:', error);
      return null;
    }
  },


  /**
   * Gets the current user's Azure AD Object ID (non-PII identifier).
   * Tries MSAL active account first, then falls back to token claims in localStorage.
   * 
   * @returns {string|null} The user's Azure AD object ID (oid) or null if not found
   */
  getCurrentUserObjectId() {
    // Try 1: Get from stored userOid (most reliable - stored during login)
    const storedOid = localStorage.getItem('userOid');
    if (storedOid) {
      return storedOid;
    }

    // Try 2: Get from MSAL active account (preferred - works for user/SSO flow)
    try {
      const activeAccount = msalInstance.getActiveAccount();
      if (activeAccount?.localAccountId) {
        return activeAccount.localAccountId;
      }
      // Fallback to first account
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && accounts[0]?.localAccountId) {
        return accounts[0].localAccountId;
      }
    } catch (e) {
      console.warn('Failed to get user ID from MSAL account');
    }

    // Try 3: Get from stored token claims in localStorage (fallback for app flow)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (/^.+-login\.windows\.net-\d.+$/.test(key)) {
        try {
          const tokenData = JSON.parse(localStorage.getItem(key));
          // Use only oid (object ID) - never email/UPN (PII)
          if (tokenData?.idTokenClaims?.oid) {
            return tokenData.idTokenClaims.oid;
          }
        } catch (e) {
          console.warn('Failed to parse token data for user ID lookup');
        }
      }
    }
    return null;
  },

  /**
   * Revokes all sign-in sessions (refresh tokens) via Microsoft Graph API.
   * This should be called before logout to ensure refresh tokens are invalidated.
   * 
   * Supports both delegated (user) flow and app flow:
   * - Both flows require userId (Azure AD object ID) for application-level session revocation
   * - User ID is auto-resolved from MSAL account or token claims (no PII used)
   * 
   * Note: Access tokens are stateless and cannot be revoked. They remain valid until expiry (~1 hour).
   * By revoking refresh tokens, we prevent new access tokens from being issued after logout.
   * 
   * @param {string} [userId] - Optional. Azure AD object ID. Auto-resolved if not provided.
   * @returns {Promise<{status: string, message: string, revoked: boolean}>}
   */
  async revokeSignInSessions(userId = null) {
    try {
      console.log('Revoking sign-in sessions...');

      // Get custom token from localStorage
      const customToken = localStorage.getItem('accessToken');
      
      if (!customToken) {
        console.log('No authentication available, skipping session revocation');
        return { status: 'skipped', message: 'No authentication available', revoked: false };
      }

      const headers = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${customToken}`
        }
      };

      // Resolve userId (Azure AD object ID) for user/delegated flow
      // Backend always requires userId since it uses application-level permissions
      let targetUserId = userId;
      
      if (!targetUserId) {
        // Auto-resolve user's Azure AD object ID (non-PII) from MSAL account or token claims
        targetUserId = this.getCurrentUserObjectId();
        if (!targetUserId) {
          console.warn('Could not resolve user object ID for session revocation');
          return { 
            status: 'Error', 
            message: 'User ID could not be resolved for logout', 
            revoked: false 
          };
        }
        console.log('Resolved user object ID for session revocation (user flow)');
      }

      // Build request body with userId (always required by backend)
      const requestBody = { userId: targetUserId };

      const response = await Axios.post(`${API_URL}/logout`, requestBody, headers);
      
      console.log('Sign-in sessions revoked successfully');
      return response.data;
    } catch (error) {
      // Log the error but don't block the logout flow
      // Even if revocation fails, we should still proceed with local logout
      console.error('Failed to revoke sign-in sessions:', error.response?.data?.message || error.message);
      return { 
        status: 'Error', 
        message: error.response?.data?.message || error.message, 
        revoked: false 
      };
    }
  },

  /**
   * Admin function: Revokes all sign-in sessions (refresh tokens) for a specific user.
   * This forces the specified user to re-authenticate on their next request.
   * 
   * Requires admin privileges and User.ReadWrite.All permission.
   * 
   * @param {string} userId - The user's Azure AD object ID (not email/UPN to avoid PII)
   * @returns {Promise<{status: string, message: string, revoked: boolean, userId?: string}>}
   */
  async logoutUser(userId) {
    try {
      console.log('Revoking sign-in sessions for target user');
      
      // Get custom token from localStorage
      const customToken = localStorage.getItem('accessToken');
      
      if (!customToken) {
        console.log('No authentication available');
        return { status: 'Error', message: 'No authentication available', revoked: false };
      }

      const headers = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${customToken}`
        }
      };

      const response = await Axios.post(`${API_URL}/logoutUser`, { userId }, headers);
      
      console.log('Sign-in sessions revoked successfully for target user');
      return response.data;
    } catch (error) {
      console.error('Failed to revoke sign-in sessions for target user:', error.response?.data?.message || error.message);
      return { 
        status: 'Error', 
        message: error.response?.data?.message || error.message, 
        revoked: false 
      };
    }
  }
}


export default apiService;


